/*
 * Before / After comparison slider.
 *
 * A transparent <input type="range"> alone is not reliable on iOS Safari: a
 * touch that lands on the track never starts a thumb drag there, so the
 * boundary would only jump on tap. The gesture is therefore taken on the frame
 * itself, and the range is kept as the keyboard and accessibility control with
 * its value mirrored both ways.
 *
 * Touch and Pointer Events are both wired up, but they never overlap: where
 * touch events exist they own every finger, because that is the path iOS
 * Safari handles dependably, and pointer events are left to mouse and pen.
 * On a device that reports pointer events but no touch events, pointer covers
 * touch as well. A gesture is also claimed by its source, so a stray event
 * from the other API can never move the boundary twice.
 *
 * Touch is deliberately not committed on touchstart. The gesture stays
 * undecided until it is clearly horizontal, so a vertical swipe over the image
 * still scrolls the page; preventDefault is called only once the horizontal
 * intent is confirmed. A touch that ends without moving counts as a tap and
 * jumps to that position.
 *
 * State lives on each element, so any number of sliders can share a page and a
 * theme-editor section reload just re-runs connectedCallback.
 */
(function () {
  var HORIZONTAL_INTENT = 6; // px of horizontal travel before we take the gesture
  var VERTICAL_INTENT = 8; // px of vertical travel before we hand it back

  if (customElements.get('before-after-slider')) return;

  customElements.define(
    'before-after-slider',
    class BeforeAfterSlider extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('.before-after__range');
        this.frame = this.querySelector('.before-after__frame');
        if (!this.range || !this.frame || this.listeners) return;

        this.listeners = [];
        this.supportsTouch = 'ontouchstart' in window;
        this.endGesture();

        this.on(this.range, 'input', this.render.bind(this));

        if (window.PointerEvent) {
          this.on(this.frame, 'pointerdown', this.onPointerDown.bind(this));
          this.on(this.frame, 'pointermove', this.onPointerMove.bind(this));
          this.on(this.frame, 'pointerup', this.onPointerEnd.bind(this));
          this.on(this.frame, 'pointercancel', this.onPointerEnd.bind(this));
        } else {
          this.on(this.frame, 'mousedown', this.onMouseDown.bind(this));
          this.on(document, 'mousemove', this.onMouseMove.bind(this));
          this.on(document, 'mouseup', this.onMouseUp.bind(this));
        }

        if (this.supportsTouch) {
          this.on(this.frame, 'touchstart', this.onTouchStart.bind(this), { passive: true });
          this.on(this.frame, 'touchmove', this.onTouchMove.bind(this), { passive: false });
          this.on(this.frame, 'touchend', this.onTouchEnd.bind(this));
          this.on(this.frame, 'touchcancel', this.onTouchEnd.bind(this));
        }

        // Only once the handlers are live, so the native range stays usable if
        // this script never runs.
        this.classList.add('before-after--enhanced');
        this.render();
      }

      disconnectedCallback() {
        if (!this.listeners) return;
        this.listeners.forEach(function (l) {
          l[0].removeEventListener(l[1], l[2], l[3]);
        });
        this.listeners = null;
        this.classList.remove('before-after--enhanced');
      }

      on(target, type, handler, options) {
        target.addEventListener(type, handler, options);
        this.listeners.push([target, type, handler, options]);
      }

      /* ---- gesture bookkeeping ---- */

      beginGesture(source, x, y, commitNow) {
        if (this.source) return false; // already owned by the other API
        this.source = source;
        this.startX = x;
        this.startY = y;
        this.pending = !commitNow;
        this.dragging = false;
        if (commitNow) this.startDrag(x);
        return true;
      }

      trackMove(source, x, y) {
        if (this.source !== source) return;

        if (this.dragging) {
          this.commit(x);
          return;
        }
        if (!this.pending) return;

        var dx = Math.abs(x - this.startX);
        var dy = Math.abs(y - this.startY);
        if (dx > dy && dx > HORIZONTAL_INTENT) {
          this.pending = false;
          this.startDrag(x);
        } else if (dy > VERTICAL_INTENT) {
          this.endGesture(); // vertical swipe: leave it to the page
        }
      }

      endGesture() {
        this.source = null;
        this.pending = false;
        this.dragging = false;
        this.pointerId = null;
        this.rect = null;
      }

      /* ---- pointer (mouse and pen; also touch where touch events are absent) ---- */

      onPointerDown(event) {
        if (!event.isPrimary || event.button > 0) return;
        if (event.pointerType === 'touch' && this.supportsTouch) return;

        var isTouch = event.pointerType === 'touch';
        if (!this.beginGesture('pointer', event.clientX, event.clientY, !isTouch)) return;
        this.pointerId = event.pointerId;

        if (!isTouch) {
          event.preventDefault(); // no text selection or native image drag
          this.range.focus({ preventScroll: true });
        }
        try {
          this.frame.setPointerCapture(event.pointerId);
        } catch (error) {
          // Capture is an optimisation; the frame still receives the events.
        }
      }

      onPointerMove(event) {
        if (event.pointerId !== this.pointerId) return;
        this.trackMove('pointer', event.clientX, event.clientY);
      }

      onPointerEnd(event) {
        if (event.pointerId !== this.pointerId) return;
        if (event.type === 'pointerup' && this.source === 'pointer' && this.pending && !this.dragging) {
          this.commit(event.clientX);
        }
        try {
          if (this.frame.hasPointerCapture(event.pointerId)) this.frame.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Already released.
        }
        this.endGesture();
      }

      /* ---- touch ---- */

      onTouchStart(event) {
        if (event.touches.length !== 1) {
          this.endGesture(); // a second finger ends the comparison gesture
          return;
        }
        this.beginGesture('touch', event.touches[0].clientX, event.touches[0].clientY, false);
      }

      onTouchMove(event) {
        // Only after the gesture is confirmed horizontal, so a vertical swipe
        // is still the page's to scroll.
        if (this.dragging) event.preventDefault();

        var touch = event.touches[0];
        if (touch) this.trackMove('touch', touch.clientX, touch.clientY);
      }

      onTouchEnd(event) {
        if (this.source !== 'touch') return;
        var touch = event.changedTouches && event.changedTouches[0];
        if (event.type === 'touchend' && this.pending && !this.dragging && touch) this.commit(touch.clientX);
        this.endGesture();
      }

      /* ---- mouse fallback, for browsers without Pointer Events ---- */

      onMouseDown(event) {
        if (event.button > 0) return;
        if (!this.beginGesture('mouse', event.clientX, event.clientY, true)) return;
        event.preventDefault();
        this.range.focus({ preventScroll: true });
      }

      onMouseMove(event) {
        this.trackMove('mouse', event.clientX, event.clientY);
      }

      onMouseUp(event) {
        if (this.source !== 'mouse') return;
        this.endGesture();
        void event;
      }

      /* ---- position ---- */

      startDrag(clientX) {
        this.dragging = true;
        this.rect = this.frame.getBoundingClientRect();
        this.commit(clientX);
      }

      commit(clientX) {
        var rect = this.rect || this.frame.getBoundingClientRect();
        if (!rect.width) return;

        var percent = ((clientX - rect.left) / rect.width) * 100;
        percent = Math.round(Math.min(100, Math.max(0, percent)));
        if (this.range.value !== String(percent)) this.range.value = percent;
        this.render();
      }

      render() {
        var value = this.range.value;
        this.frame.style.setProperty('--ba-position', value + '%');
        this.range.setAttribute('aria-valuetext', value + '%');
      }
    }
  );
})();
